import React from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import { consola } from "consola";

import StandardButton from "../../../../components/StandardButton";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import { RecipeGenerationMessage } from "../../../../data/ChatGPTMessages";

const MacroInputStack = ({
  label,
  defaultValues,
  onMinChanged,
  onMaxChanged,
}) => {
  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="subtitle2" sx={{ mb: 0.55, fontWeight: 600, fontSize: '0.8rem' }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <TextField
            label={`Min ${label}`}
            size="small"
            value={defaultValues.min}
            variant="outlined"
            type="number"
            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
            onChange={(event) => {
              const value = event.target.value.length === 0 ? 0 : parseInt(event.target.value, 10);
              onMinChanged(isNaN(value) ? 0 : value);
            }}
          />
          <TextField
            label={`Max ${label}`}
            size="small"
            value={defaultValues.max}
            variant="outlined"
            type="number"
            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
            onChange={(event) => {
              const value = event.target.value.length === 0 ? 0 : parseInt(event.target.value, 10);
              onMaxChanged(isNaN(value) ? 0 : value);
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const DIETARY_OPTIONS = [
  { value: "balanced", label: "Balanced" },
  { value: "keto", label: "Keto" },
  { value: "atkins", label: "Atkins" },
  { value: "low carb", label: "Low Carb" },
  { value: "high carb", label: "High Carb" },
  { value: "fat free", label: "Fat Free" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten free", label: "Gluten Free" },
  { value: "dairy free", label: "Dairy Free" },
  { value: "paleo", label: "Paleo" },
  { value: "clean", label: "Clean" },
  { value: "mediterranean", label: "Mediterranean" },
];

const MEAL_TYPE_OPTIONS = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "snack", label: "Snack" },
];

const DietaryRestrictionsSelector = ({ selectedDiets, onDietsChanged }) => {
  const toggleDiet = (dietValue) => {
    if (selectedDiets.includes(dietValue)) {
      onDietsChanged(selectedDiets.filter(diet => diet !== dietValue));
    } else {
      onDietsChanged([...selectedDiets, dietValue]);
    }
  };

  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.8rem' }}>
          Dietary Restrictions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
          {DIETARY_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              size="small"
              color={selectedDiets.includes(option.value) ? "primary" : "default"}
              variant={selectedDiets.includes(option.value) ? "filled" : "outlined"}
              onClick={() => toggleDiet(option.value)}
              sx={{ 
                fontSize: '0.7rem',
                height: '24px',
                '&:hover': { 
                  backgroundColor: selectedDiets.includes(option.value) 
                    ? 'primary.dark' 
                    : 'action.hover' 
                }
              }}
            />
          ))}
        </Box>
        {selectedDiets.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.65rem' }}>
            Select one or more dietary restrictions, or leave empty for no restrictions
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const MealTypeSelector = ({ selectedMealType, onMealTypeChanged }) => {
  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.8rem' }}>
          Meal Type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
          {MEAL_TYPE_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              size="small"
              color={selectedMealType === option.value ? "primary" : "default"}
              variant={selectedMealType === option.value ? "filled" : "outlined"}
              onClick={() => onMealTypeChanged(option.value)}
              sx={{ 
                fontSize: '0.7rem',
                height: '24px',
                '&:hover': { 
                  backgroundColor: selectedMealType === option.value 
                    ? 'primary.dark' 
                    : 'action.hover' 
                }
              }}
            />
          ))}
        </Box>
        {!selectedMealType && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.65rem' }}>
            Select a meal type for better recipe suggestions
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const CustomInstructionsInput = ({ customInstructions, onCustomInstructionsChanged }) => {
  return (
    <Card sx={{ mb: 1, p: 0.5 }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
          Custom Instructions
        </Typography>
        <TextField
          multiline
          rows={2}
          fullWidth
          size="small"
          placeholder='e.g., "Soups and smoothies only", "No nuts", "Quick 15-min meals"'
          value={customInstructions}
          onChange={(event) => onCustomInstructionsChanged(event.target.value)}
          variant="outlined"
          sx={{ fontSize: '0.875rem' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
          Add any specific preferences or requirements for your recipe
        </Typography>
      </CardContent>
    </Card>
  );
};

const RecipeGeneratorDialog = ({ recipes, onDialogClosed }) => {
  const [kCals, setKCals] = React.useState({ min: 0, max: 0 });
  const [proteins, setProteins] = React.useState({ min: 0, max: 0 });
  const [carbs, setCarbs] = React.useState({ min: 0, max: 0 });
  const [fats, setFats] = React.useState({ min: 0, max: 0 });
  const [selectedDiets, setSelectedDiets] = React.useState([]);
  const [selectedMealType, setSelectedMealType] = React.useState("");
  const [customInstructions, setCustomInstructions] = React.useState("");

  const [foundRecipe, setFoundRecipe] = React.useState(null);
  const [searching, setSearching] = React.useState(false);

  const generateRecipes = () => {
    setSearching(true);
    // create object of mins and maxes for each macro but only if they are not 0
    const macros = {};
    if (kCals.min !== 0 || kCals.max !== 0) {
      macros.kCals = kCals;
    }
    if (proteins.min !== 0 || proteins.max !== 0) {
      macros.proteins = proteins;
    }
    if (carbs.min !== 0 || carbs.max !== 0) {
      macros.carbs = carbs;
    }
    if (fats.min !== 0 || fats.max !== 0) {
      macros.fats = fats;
    }

    // make a string of the macros with more precise targeting language
    let macrosString = "";
    if (Object.keys(macros).length > 0) {
      const macroDescriptions = [];
      Object.keys(macros).forEach((key) => {
        const min = macros[key].min;
        const max = macros[key].max;
        
        if (min > 0 && max > 0) {
          if (min === max) {
            macroDescriptions.push(`exactly ${min} ${key} per serving`);
          } else {
            macroDescriptions.push(`${min}-${max} ${key} per serving`);
          }
        } else if (min > 0) {
          macroDescriptions.push(`at least ${min} ${key} per serving`);
        } else if (max > 0) {
          macroDescriptions.push(`no more than ${max} ${key} per serving`);
        }
      });
      macrosString = macroDescriptions.join(", ");
    }

    // Convert dietary restrictions array to string
    const dietaryRestrictions = selectedDiets.length > 0 ? selectedDiets.join(", ") : "balanced";

    const messages = RecipeGenerationMessage({ 
      macrosString, 
      diet: dietaryRestrictions, 
      mealType: selectedMealType,
      customInstructions,
      recipes 
    });

    getChatGPTResponse({ messages: messages, temperature: 0.5 })
      .then((response) => {
        try {
          const contentObject = JSON.parse(response.response[0].message.content);
          
          // Validate that required fields exist
          if (!contentObject.n || !contentObject.d || !contentObject.ns || !contentObject.mac || !contentObject.ing || !contentObject.i) {
            throw new Error("Missing required recipe fields");
          }
          
          // Validate ingredient structure
          const hasValidIngredients = contentObject.ing.every(ing => 
            ing.n && ing.q !== undefined && ing.u && ing.mac && 
            typeof ing.mac.p === 'number' && typeof ing.mac.c === 'number' && 
            typeof ing.mac.f === 'number' && typeof ing.mac.kCals === 'number'
          );
          
          if (!hasValidIngredients) {
            consola.log("Invalid ingredient structure - some ingredients missing macro data");
          }
          
          setFoundRecipe(contentObject);
          setSearching(false);
        } catch (parseError) {
          consola.log("JSON parsing error:", parseError);
          consola.log("Raw response:", response.response[0].message.content);
          setSearching(false);
          // You could set an error state here to show the user
        }
      })
      .catch((error) => {
        consola.log("API error:", error);
        setSearching(false);
      });
  };

  const getMacroPerServing = (macro, servings) => {
    return (macro / parseInt(servings, 10)).toFixed(1);
  };

  return (
    <Dialog
      maxWidth="xl"
      fullWidth={true}
      open={true}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: 600
      }}>
        🍽️ AI Recipe Generator
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.9rem' }}>
          Set your nutritional criteria and dietary preferences, and our AI will create a personalized recipe for you.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 1.5, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600, fontSize: '1.1rem' }}>
                Nutritional Criteria (per serving)
              </Typography>
              
              <MacroInputStack
                label="Calories"
                defaultValues={kCals}
                onMinChanged={(min) => setKCals({ ...kCals, min })}
                onMaxChanged={(max) => setKCals({ ...kCals, max })}
              />
              
              <MacroInputStack
                label="Proteins (g)"
                defaultValues={proteins}
                onMinChanged={(min) => setProteins({ ...proteins, min })}
                onMaxChanged={(max) => setProteins({ ...proteins, max })}
              />
              
              <MacroInputStack
                label="Carbs (g)"
                defaultValues={carbs}
                onMinChanged={(min) => setCarbs({ ...carbs, min })}
                onMaxChanged={(max) => setCarbs({ ...carbs, max })}
              />
              
              <MacroInputStack
                label="Fats (g)"
                defaultValues={fats}
                onMinChanged={(min) => setFats({ ...fats, min })}
                onMaxChanged={(max) => setFats({ ...fats, max })}
              />

              <DietaryRestrictionsSelector
                selectedDiets={selectedDiets}
                onDietsChanged={setSelectedDiets}
              />

              <MealTypeSelector
                selectedMealType={selectedMealType}
                onMealTypeChanged={setSelectedMealType}
              />

              <CustomInstructionsInput
                customInstructions={customInstructions}
                onCustomInstructionsChanged={setCustomInstructions}
              />

              <StandardButton 
                variant="primary"
                onClick={generateRecipes} 
                fullWidth
                size="medium"
                disabled={searching}
                sx={{ mt: 1.5, py: 1 }}
              >
                {searching ? 'Generating Recipe...' : '🔍 Find Perfect Recipe'}
              </StandardButton>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 2, height: '75vh', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Generated Recipe
              </Typography>
              
              {foundRecipe && !searching && (
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  <Card sx={{ 
                    mb: 2, 
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    minHeight: 'auto',
                    height: 'auto'
                  }}>
                    <CardContent sx={{ 
                      p: 3,
                      minHeight: 'auto',
                      height: 'auto',
                      '&:last-child': { pb: 3 }
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 2 }}>
                        {foundRecipe.n}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Serves: {foundRecipe.ns} people
                        </Typography>
                        {foundRecipe.mealType && (
                          <Chip 
                            label={foundRecipe.mealType.charAt(0).toUpperCase() + foundRecipe.mealType.slice(1)} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 3, 
                          lineHeight: 1.6, 
                          fontSize: '1rem',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {foundRecipe.d}
                      </Typography>
                      
                      {foundRecipe.analysis && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            🎯 Recipe Analysis
                          </Typography>
                          <Grid container spacing={2}>
                            {foundRecipe.analysis.macro_accuracy && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <strong>Macro Accuracy:</strong> {foundRecipe.analysis.macro_accuracy}
                                </Typography>
                              </Grid>
                            )}
                            {foundRecipe.analysis.dietary_compliance && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <strong>Dietary Compliance:</strong> {foundRecipe.analysis.dietary_compliance}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          {foundRecipe.analysis.reasoning && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                              <strong>Chef&apos;s Notes:</strong> {foundRecipe.analysis.reasoning}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        📊 Nutritional Information (per serving)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'primary.light', borderRadius: 1, color: 'white' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {getMacroPerServing(foundRecipe.mac.p, foundRecipe.ns)}g
                            </Typography>
                            <Typography variant="caption">Protein</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1, color: 'white' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {getMacroPerServing(foundRecipe.mac.c, foundRecipe.ns)}g
                            </Typography>
                            <Typography variant="caption">Carbs</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.light', borderRadius: 1, color: 'white' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {getMacroPerServing(foundRecipe.mac.f, foundRecipe.ns)}g
                            </Typography>
                            <Typography variant="caption">Fats</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.light', borderRadius: 1, color: 'white' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {Math.round(foundRecipe.mac.kCals / foundRecipe.ns)}
                            </Typography>
                            <Typography variant="caption">Calories</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        🛒 Ingredients
                      </Typography>
                      <Grid container spacing={1}>
                        {foundRecipe.ing.map((ingredient, index) => (
                          <Grid item xs={12} key={index}>
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: 'grey.50', 
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {ingredient.q} {ingredient.u} - {ingredient.n}
                                  </Typography>
                                </Grid>
                                {ingredient.mac && (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip 
                                        label={`${ingredient.mac.p}g P`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                      <Chip 
                                        label={`${ingredient.mac.c}g C`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                      <Chip 
                                        label={`${ingredient.mac.f}g F`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                      <Chip 
                                        label={`${ingredient.mac.kCals} cal`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      
                      {/* Verification section to show macro totals match */}
                      {foundRecipe.ing.some(ing => ing.mac) && (
                        <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1, color: 'white' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            📊 Macro Verification: 
                            {(() => {
                              const totalP = foundRecipe.ing.reduce((sum, ing) => sum + (ing.mac?.p || 0), 0);
                              const totalC = foundRecipe.ing.reduce((sum, ing) => sum + (ing.mac?.c || 0), 0);
                              const totalF = foundRecipe.ing.reduce((sum, ing) => sum + (ing.mac?.f || 0), 0);
                              const totalCal = foundRecipe.ing.reduce((sum, ing) => sum + (ing.mac?.kCals || 0), 0);
                              
                              const matches = 
                                Math.abs(totalP - foundRecipe.mac.p) < 1 &&
                                Math.abs(totalC - foundRecipe.mac.c) < 1 &&
                                Math.abs(totalF - foundRecipe.mac.f) < 1 &&
                                Math.abs(totalCal - foundRecipe.mac.kCals) < 5;
                              
                              return matches ? 
                                " ✅ Individual ingredient macros match recipe totals" :
                                ` ⚠️ Totals: ${totalP.toFixed(1)}P, ${totalC.toFixed(1)}C, ${totalF.toFixed(1)}F, ${totalCal}cal`;
                            })()}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        👨‍🍳 Instructions
                      </Typography>
                      {foundRecipe.i.map((instruction, index) => (
                        <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                          <Box sx={{ 
                            minWidth: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}>
                            {index + 1}
                          </Box>
                          <Typography variant="body1" sx={{ flex: 1, pt: 0.5 }}>
                            {typeof instruction === 'string' ? instruction : instruction.step}
                            {instruction.duration && (
                              <Box component="div" sx={{ mt: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                                ⏱️ {instruction.duration > 60 ? `${Math.round(instruction.duration / 60)} min` : `${instruction.duration} sec`}
                              </Box>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Box>
              )}
              
              {searching && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Stack
                    spacing={3}
                    sx={{
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={80} thickness={4} />
                    <Typography variant="h5" sx={{ fontWeight: 500 }}>
                      Creating your perfect recipe...
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Our AI is analyzing your criteria and crafting a delicious recipe just for you!
                    </Typography>
                  </Stack>
                </Box>
              )}
              
              {!searching && !foundRecipe && (
                <Stack
                  spacing={3}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ fontSize: '4rem' }}>🍽️</Box>
                  <Typography variant="h5" sx={{ fontWeight: 500 }}>
                    Ready to Create Your Recipe?
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '80%' }}>
                    Set your nutritional goals and dietary preferences on the left, then click 
                    &quot;Find Perfect Recipe&quot; to get started!
                  </Typography>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={() => {
            if (onDialogClosed) {
              onDialogClosed(null);
            }
          }}
          size="large"
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            if (onDialogClosed) {
              onDialogClosed(foundRecipe);
            }
          }}
          disabled={!foundRecipe}
          size="large"
          sx={{ px: 3 }}
        >
          Use This Recipe
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeGeneratorDialog;
